import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Text } from '@rtkwlf/c3-core-react';
import {
  TimelineItem,
  TimelineItemContent,
  TimelineItemTrigger,
} from '@rtkwlf/c3-recipes-timeline';
import { Tag } from '@rtkwlf/fenrir-react';
import { faArrowsSplitUpAndLeft } from '@rtkwlf/pro-solid-svg-icons/faArrowsSplitUpAndLeft';
import { faBullseye } from '@rtkwlf/pro-solid-svg-icons/faBullseye';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useGetOrganizationSecurityFocusOptions } from '@apis/track-public/query-options';

import { routes } from '../../../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../../../locales/use-security-journey-translations';
import { FocusStatusBadge } from '../../../components/focus-status-badge';

import styles from './security-focus-timeline-focus.module.css';
import { SecurityFocusTimelineItemEmpty } from './security-focus-timeline-item-empty';
import { SecurityFocusTimelineItemError } from './security-focus-timeline-item-error';
import { SecurityFocusTimelineItemInfo } from './security-focus-timeline-item-info';
import { SecurityFocusTimelineItemLoading } from './security-focus-timeline-item-loading';

/* -------------------------------------------------------------------------------------------------
 * Constants and Utilities
 * -----------------------------------------------------------------------------------------------*/
type FocusRequestArgs = {
  focusID: string;
};

const typeToFocusAppearance = {
  focus: {
    label: 'focusID.focus',
    emptyTitle: 'focusID.timeline.focus.unknown',
    emptyDescription: 'focusID.timeline.focus.unknown_description',
    errorDescription: 'focusID.timeline.focus.error',
    icon: faBullseye,
  },
  detour: {
    label: 'focusID.detour',
    emptyTitle: 'focusID.timeline.focus.unknown_detour',
    emptyDescription: 'focusID.timeline.focus.unknown_detour_description',
    errorDescription: 'focusID.timeline.focus.error_detour',
    icon: faArrowsSplitUpAndLeft,
  },
} as const;

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusTimelineFocusContent
 * -----------------------------------------------------------------------------------------------*/
type SecurityFocusTimelineFocusContentProps = FocusRequestArgs & {
  type: keyof typeof typeToFocusAppearance;
};

const SecurityFocusTimelineFocusContent: React.FunctionComponent<
  SecurityFocusTimelineFocusContentProps
> = ({ focusID, type }) => {
  /**
   * Fetch details for the focus
   */
  const {
    data: focus,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    useGetOrganizationSecurityFocusOptions({
      focusID,
    }),
  );

  /**
   * Get an instance of translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Return the loading state for the focus
   */
  if (isLoading) {
    return <SecurityFocusTimelineItemLoading />;
  }

  /**
   * Return the error state for the focus
   */
  if (isError) {
    return (
      <SecurityFocusTimelineItemError
        icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
        description={t(typeToFocusAppearance[type].errorDescription)}
        onRetry={refetch}
      />
    );
  }

  /**
   * Return an empty state if the focus is undefined
   */
  if (!focus) {
    return (
      <SecurityFocusTimelineItemEmpty
        description={t(typeToFocusAppearance[type].emptyDescription)}
        icon={<FontAwesomeIcon icon={typeToFocusAppearance[type].icon} />}
        title={t(typeToFocusAppearance[type].emptyTitle)}
      />
    );
  }

  /**
   * Return the contents of the focus
   */
  return (
    <>
      <header className={styles['sft-focus-item-header']}>
        <div className={styles['sft-focus-item-header-title']}>
          <Text as="div" size="3" weight="bold">
            {focus.name}
          </Text>
          {type === 'detour' && (
            <Tag variant="secondary" appearance="neutral">
              {t('focusID.detour')}
            </Tag>
          )}
        </div>
        <FocusStatusBadge
          className={styles['sft-focus-item-header-badge']}
          state={focus.state}
        />
      </header>
      <div className={styles['sft-focus-item-info-container']}>
        <SecurityFocusTimelineItemInfo
          heading={t('focusID.timeline.focus.started_completed')}
          value={t('focusID.timeline.spidr', {
            count: focus.spidrs.length,
          })}
        />
        <SecurityFocusTimelineItemInfo
          heading={t('focusID.timeline.upcoming')}
          value={t('focusID.timeline.spidr', {
            count: focus.blueprints.length,
          })}
        />
      </div>
    </>
  );
};

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusTimeline
 * -----------------------------------------------------------------------------------------------*/

type SecurityFocusTimelineFocusProps = FocusRequestArgs & {
  type: keyof typeof typeToFocusAppearance;
};

const SecurityFocusTimelineFocus: React.FunctionComponent<
  SecurityFocusTimelineFocusProps
> = ({ focusID, type }) => {
  /**
   * Get an instance of the query client
   */
  const queryClient = useQueryClient();

  /**
   * Get focus query options
   */
  const focusQueryOptions = useGetOrganizationSecurityFocusOptions({
    focusID,
  });

  /**
   * Pretfetch the focus details when hovering the timeline item
   */
  const onMouseEnterTrigger = () => {
    void queryClient.prefetchQuery({ ...focusQueryOptions, staleTime: 5000 });
  };

  /**
   * Get an instance of translations
   */
  const { t } = useSecurityJourneyTranslations();

  return (
    <TimelineItem>
      <TimelineItemTrigger
        asChild
        attributes={{ onMouseEnter: onMouseEnterTrigger }}
        color="grey"
        label={`${t(typeToFocusAppearance[type].label)}`}
        size="sm"
        variant="secondary"
      >
        <Link
          to={routes.securityFocuses.focusID.index({
            focusID,
          })}
        >
          <FontAwesomeIcon icon={typeToFocusAppearance[type].icon} />
        </Link>
      </TimelineItemTrigger>
      <TimelineItemContent
        className={styles['sft-focus-item-content']}
        data-testid="security-focus-timeline-item-focus"
      >
        <SecurityFocusTimelineFocusContent focusID={focusID} type={type} />
      </TimelineItemContent>
    </TimelineItem>
  );
};

export { SecurityFocusTimelineFocus };
export type { SecurityFocusTimelineFocusProps };
